/**
 * Validação dos formulários — espelho dos schemas Pydantic de
 * `catalog/presentation/schemas.py`. Limites idênticos: o que passa aqui
 * passa lá, e o que a API recusaria é recusado antes de sair do navegador.
 */
import { z } from 'zod';
import { LessonKind } from './course';
import { slugFromTitle } from './slug';

/** Os mesmos números de `CreateCourseIn`, `AddModuleIn` e `AddLessonIn`. */
export const LIMITS = {
  courseTitle: { min: 3, max: 300 },
  title: 300,
  description: 5000,
  tags: 20,
  tag: 40,
  /** `duration_s ≤ 86.400` — 24 h. */
  durationMin: 1440,
  /** Quem ministra o curso (`author_name`). */
  authorName: 120,
} as const;

const max = (n: number) => `No máximo ${n.toLocaleString('pt-BR')} caracteres`;

export const createCourseSchema = z.object({
  title: z
    .string()
    .trim()
    .min(LIMITS.courseTitle.min, `Mínimo de ${LIMITS.courseTitle.min} caracteres`)
    .max(LIMITS.courseTitle.max, max(LIMITS.courseTitle.max))
    // O endereço do curso nasce do título: "!!!" não geraria nenhum.
    .refine((v) => v.length < LIMITS.courseTitle.min || slugFromTitle(v) !== '', {
      message: 'Use ao menos uma letra ou número',
    }),
  description: z.string().max(LIMITS.description, max(LIMITS.description)),
  tags: z
    .string()
    .refine((v) => parseTags(v).every((t) => t.length <= LIMITS.tag), {
      message: `Cada tag pode ter até ${LIMITS.tag} caracteres`,
    })
    .refine((v) => parseTags(v).length <= LIMITS.tags, { message: `No máximo ${LIMITS.tags} tags` }),
});
export type CreateCourseForm = z.infer<typeof createCourseSchema>;

const itemTitle = z.string().trim().min(1, 'Informe o título').max(LIMITS.title, max(LIMITS.title));

export const addModuleSchema = z.object({ title: itemTitle });
export type AddModuleForm = z.infer<typeof addModuleSchema>;

export const addLessonSchema = z.object({
  title: itemTitle,
  description: z.string().max(LIMITS.description, max(LIMITS.description)),
  /** Minutos inteiros, como digitados. Vazio vale 0. Vídeo ignora: a duração vem do arquivo. */
  duration: z
    .string()
    .refine((v) => v === '' || /^\d+$/.test(v), { message: 'Use só números inteiros' })
    .refine((v) => v === '' || Number(v) <= LIMITS.durationMin, {
      message: `No máximo ${LIMITS.durationMin.toLocaleString('pt-BR')} minutos (24 h)`,
    }),
  kind: z.enum([LessonKind.VIDEO, LessonKind.TEXT, LessonKind.QUIZ, LessonKind.LIVE]),
  required: z.boolean(),
});
export type AddLessonForm = z.infer<typeof addLessonSchema>;

export const emptyLesson = (): AddLessonForm => ({
  title: '',
  description: '',
  duration: '',
  kind: LessonKind.VIDEO,
  required: true,
});

// ── Edição no lugar (`UpdateCourseIn`, `RenameModuleIn`, `UpdateLessonIn`) ──
// Campo a campo, com as mesmas regras da criação: o editor do curso salva
// cada um sozinho, então cada um se valida sozinho.

const authorName = z.string().trim().max(LIMITS.authorName, max(LIMITS.authorName));

/** A primeira mensagem do schema para o valor, ou `null` se ele serve. */
function problemOf(schema: z.ZodType, value: string): string | null {
  const result = schema.safeParse(value);
  return result.success ? null : (result.error.issues[0]?.message ?? 'Valor inválido');
}

export const courseTitleProblem = (v: string) => problemOf(createCourseSchema.shape.title, v);
export const itemTitleProblem = (v: string) => problemOf(itemTitle, v);
export const descriptionProblem = (v: string) => problemOf(createCourseSchema.shape.description, v);
export const tagsProblem = (v: string) => problemOf(createCourseSchema.shape.tags, v);
export const authorNameProblem = (v: string) => problemOf(authorName, v);

/** O que muda no curso — o corpo de `UpdateCourseIn`. Ausente: fica como está. */
export interface CourseChanges {
  title?: string;
  description?: string;
  tags?: string[];
  /** `null` apaga. */
  author_name?: string | null;
}

/** O que muda na aula — o corpo de `UpdateLessonIn`. */
export interface LessonChanges {
  title?: string;
  description?: string;
}

/** Autor(a) como a API guarda: sem espaços sobrando; vazio vira `null` (apaga). */
export const toAuthorName = (raw: string): string | null => collapseSpaces(raw) || null;

/** O corpo de `AddLessonIn`. `seconds`: a duração lida do arquivo de vídeo, quando houver. */
export function toLessonBody(form: AddLessonForm, seconds?: number | null) {
  const typed = Number(form.duration || 0) * 60;
  return {
    title: collapseSpaces(form.title),
    description: form.description.trim(),
    duration_s: Math.min(Math.max(0, Math.round(seconds ?? typed)), LIMITS.durationMin * 60),
    kind: form.kind,
    required: form.required,
  };
}

/** O corpo de `CreateCourseIn`. O endereço (slug) a API gera a partir do título. */
export const toCourseBody = (form: CreateCourseForm) => ({
  title: collapseSpaces(form.title),
  description: form.description.trim(),
  slug: null,
  tags: parseTags(form.tags),
});

// ── Formatação dos campos ──────────────────────────────────────────

/** Espaços repetidos e das pontas — o mesmo que a API faz com títulos. */
export const collapseSpaces = (v: string) => v.replace(/\s+/g, ' ').trim();

/** Tags como a API guarda: minúsculas, sem espaços sobrando, sem vazias nem repetidas. */
export function parseTags(raw: string): string[] {
  return [
    ...new Set(
      raw
        .split(',')
        .map((t) => collapseSpaces(t).toLowerCase())
        .filter(Boolean),
    ),
  ];
}

/** O campo de tags arrumado ao sair dele: "Vendas,  vendas ,Metas" → "vendas, metas". */
export const formatTags = (raw: string) => parseTags(raw).join(', ');

/** Minutos: só dígitos, sem zero à esquerda. */
export const digitsOnly = (v: string) => v.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
